import React from 'react'
import { Document, Page, Text, View, StyleSheet, Svg, Path, G, Defs, ClipPath, Rect } from '@react-pdf/renderer'

const Colors = {
    DarkText: '#35323C',
    Secondary: '#6E6C71',
    OffWhite: '#F8F8F8',
    GreenBright: '#80D387',
    BlueBright: '#5AB4F1',
    Gray2: '#C7C7CC',
    OrangeBright: '#FF7A3B',
    Primary600: '#186ED7',
    Primary900: '#022755',
    Pink: '#FF5159',
    White: '#FFFFFF',
    Gray5: '#EFEFF4',
}

const pdfTranslations = {
    en: {
        student: 'Student',
        overallProgression: 'Overall Course Progression',
        lesson: 'Lesson',
        status: 'Status',
        score: 'Score',
        time: 'Time',
        date: 'Date',
        exam: 'EXAM',
        passed: 'Passed',
        notPassed: 'Not passed',
        noLessonData: 'No lesson data available',
        footerTitle: 'Capeesh \u2014 Course Progression Report',
        page: 'Page',
    },
    nb: {
        student: 'Elev',
        overallProgression: 'Total kursfremdrift',
        lesson: 'Leksjon',
        status: 'Status',
        score: 'Poeng',
        time: 'Tid',
        date: 'Dato',
        exam: 'EKSAMEN',
        passed: 'Best\u00e5tt',
        notPassed: 'Ikke best\u00e5tt',
        noLessonData: 'Ingen leksjonsdata tilgjengelig',
        footerTitle: 'Capeesh \u2014 Kursfremdriftsrapport',
        page: 'Side',
    },
    de: {
        student: 'Sch\u00fcler',
        overallProgression: 'Gesamter Kursfortschritt',
        lesson: 'Lektion',
        status: 'Status',
        score: 'Punkte',
        time: 'Zeit',
        date: 'Datum',
        exam: 'PR\u00dcFUNG',
        passed: 'Bestanden',
        notPassed: 'Nicht bestanden',
        noLessonData: 'Keine Lektionsdaten verf\u00fcgbar',
        footerTitle: 'Capeesh \u2014 Kursfortschrittsbericht',
        page: 'Seite',
    },
}

const styles = StyleSheet.create({
    page: {
        paddingTop: 0,
        paddingBottom: 40,
        paddingHorizontal: 40,
        fontFamily: 'Helvetica',
        fontSize: 10,
        color: Colors.DarkText,
        backgroundColor: Colors.White,
    },
    banner: {
        backgroundColor: Colors.Primary900,
        paddingVertical: 12,
        paddingHorizontal: 40,
        marginHorizontal: -40,
        marginBottom: 20,
    },
    header: {
        marginBottom: 24,
        paddingBottom: 16,
        borderBottomWidth: 2,
        borderBottomColor: Colors.Primary600,
        borderBottomStyle: 'solid',
    },
    courseTitle: {
        fontSize: 22,
        fontFamily: 'Helvetica-Bold',
        color: Colors.Primary600,
        marginBottom: 4,
    },
    courseDescription: {
        fontSize: 11,
        color: Colors.Secondary,
        marginBottom: 12,
    },
    headerMeta: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 8,
    },
    metaText: {
        fontSize: 9,
        color: Colors.Secondary,
    },
    overallProgressSection: {
        marginBottom: 20,
        padding: 14,
        backgroundColor: Colors.OffWhite,
        borderRadius: 8,
    },
    overallProgressLabel: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        color: Colors.Secondary,
        marginBottom: 6,
    },
    overallProgressBarTrack: {
        height: 14,
        backgroundColor: Colors.Gray2,
        borderRadius: 7,
        overflow: 'hidden',
        position: 'relative',
    },
    overallProgressPercentage: {
        fontSize: 12,
        fontFamily: 'Helvetica-Bold',
        color: Colors.DarkText,
        marginTop: 4,
        textAlign: 'right',
    },
    topicSection: {
        marginBottom: 16,
        borderRadius: 8,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: Colors.Gray5,
        borderStyle: 'solid',
    },
    topicHeader: {
        padding: 12,
        backgroundColor: Colors.OffWhite,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    topicNameRow: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
    topicName: {
        fontSize: 13,
        fontFamily: 'Helvetica-Bold',
        color: Colors.DarkText,
    },
    topicDescription: {
        fontSize: 9,
        color: Colors.Secondary,
        marginTop: 2,
    },
    topicProgressContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 6,
    },
    topicProgressBarTrack: {
        height: 10,
        width: 180,
        backgroundColor: Colors.Gray2,
        borderRadius: 5,
        overflow: 'hidden',
        position: 'relative',
    },
    topicProgressPercentage: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        color: Colors.DarkText,
        marginLeft: 8,
        width: 36,
    },
    lessonTable: {
        padding: '0 12px 12px 12px',
    },
    lessonTableHeader: {
        flexDirection: 'row',
        paddingVertical: 6,
        paddingHorizontal: 8,
        backgroundColor: Colors.Primary600,
        borderRadius: 4,
        marginBottom: 2,
    },
    lessonTableHeaderText: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        color: Colors.White,
    },
    lessonRow: {
        flexDirection: 'row',
        paddingVertical: 5,
        paddingHorizontal: 8,
        borderRadius: 3,
        alignItems: 'center',
    },
    lessonRowEven: {
        backgroundColor: Colors.OffWhite,
    },
    lessonRowOdd: {
        backgroundColor: Colors.White,
    },
    colPassed: { width: '12%', textAlign: 'center' },
    colScore: { width: '14%', textAlign: 'center' },
    colTime: { width: '16%', textAlign: 'right' },
    colDate: { width: '20%', textAlign: 'right' },
    lessonText: {
        fontSize: 9,
        color: Colors.DarkText,
    },
    lessonNameRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    examBadge: {
        backgroundColor: Colors.OrangeBright,
        borderRadius: 3,
        paddingHorizontal: 5,
        paddingVertical: 1,
        marginLeft: 5,
    },
    examBadgeText: {
        fontSize: 7,
        fontFamily: 'Helvetica-Bold',
        color: Colors.White,
    },
    passedText: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        color: Colors.GreenBright,
    },
    notPassedText: {
        fontSize: 9,
        color: Colors.Pink,
    },
    noLessonsText: {
        fontSize: 9,
        color: Colors.Secondary,
        fontStyle: 'italic',
        padding: '6px 12px 12px 12px',
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 40,
        right: 40,
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderTopWidth: 1,
        borderTopColor: Colors.Gray5,
        borderTopStyle: 'solid',
        paddingTop: 8,
    },
    footerText: {
        fontSize: 8,
        color: Colors.Gray2,
    },
})

const ProgressBar = ({ value, width, height, trackColor, fillColor, borderRadius }) => (
    <View style={{
        height: height || 10,
        width: width || '100%',
        backgroundColor: trackColor || Colors.Gray2,
        borderRadius: borderRadius || 5,
        overflow: 'hidden',
    }}>
        <View style={{
            height: '100%',
            width: `${Math.min(Math.max(value * 100, 0), 100)}%`,
            backgroundColor: fillColor || Colors.BlueBright,
            borderRadius: borderRadius || 5,
        }} />
    </View>
)

const CapeeshLogoBanner = () => (
    <View style={styles.banner} fixed>
        <Svg width={120} height={27.4} viewBox="0 0 315 72">
            <Defs>
                <ClipPath id="clip0">
                    <Rect width="315" height="71.9896" />
                </ClipPath>
            </Defs>
            <G clipPath="url(#clip0)">
                <Path
                    fill="white"
                    fillRule="evenodd"
                    d="M19.3911 21.722C19.3911 21.722 22.6563 14.2359 24.7244 9.49339C25.1271 8.56677 25.9287 7.87364 26.9061 7.60832C27.8797 7.34336 28.923 7.53476 29.7429 8.12945C33.9488 11.1893 40.6146 16.0361 40.6146 16.0361L40.9403 15.949C56.6474 11.7477 61.9734 15.1014 65.8279 29.5265C66.3916 31.6315 67.0396 34.0813 67.6509 36.3316C71.542 50.6725 67.7241 56.2714 52.0829 60.4613C45.4537 62.2373 37.8985 64.2702 31.2694 66.0375C16.1077 70.0792 10.2948 68.716 5.97908 52.853C5.40439 50.7458 4.71622 48.143 4.15617 46.0479C0.316326 31.7604 3.96585 25.7857 18.9153 21.8497L19.3911 21.722ZM28.2696 14.1286C28.3758 13.8271 28.6283 13.5976 28.9358 13.5157C29.2425 13.4308 29.5763 13.5011 29.8202 13.7076C29.8202 13.7076 37.1323 19.3498 39.4079 21.1063C39.8306 21.4298 40.3781 21.5389 40.8929 21.3966C41.1232 21.3334 41.3169 21.28 41.5041 21.2284L41.5048 21.2282C41.7571 21.1587 41.9977 21.0924 42.2999 21.0098C54.313 17.7247 57.6765 20.2817 60.4493 30.4243C60.5954 30.9572 60.7972 31.6893 61.0182 32.4912C61.4755 34.1497 62.015 36.1068 62.314 37.2184C64.8753 46.71 63.3584 51.1481 51.4661 54.4366C48.6271 55.2215 44.7146 56.2983 40.8264 57.3675C36.9314 58.4118 33.0117 59.4619 30.1657 60.2208C18.2435 63.3992 14.6929 60.3372 12.1086 50.8518C11.8051 49.7415 11.282 47.7798 10.8387 46.1175C10.6245 45.3138 10.4288 44.5801 10.2857 44.0464C7.55494 33.8924 9.16552 29.9847 21.1902 26.7423C21.4921 26.6607 21.7328 26.5963 21.9852 26.5288L21.9871 26.5283L21.9876 26.5282C22.1751 26.4781 22.3691 26.4262 22.5995 26.3642C23.1156 26.2266 23.5329 25.8555 23.7343 25.3626C24.8104 22.6958 28.2696 14.1286 28.2696 14.1286Z"
                />
                <Path
                    fill="white"
                    d="M27.1788 41.5699C26.6919 39.7357 24.8031 38.643 22.9692 39.1315L21.1573 39.6142C19.3234 40.1031 18.2289 41.9893 18.7194 43.8239L19.8798 48.1836C20.3703 50.0178 22.2554 51.1106 24.0893 50.622L25.9013 50.1393C27.7388 49.6504 28.8296 47.7642 28.3428 45.9296L27.1788 41.5699Z"
                />
                <Path
                    fill="white"
                    d="M48.3332 32.3434C53.7434 30.922 56.3826 35.1284 57.3783 37.3556C57.5173 37.6729 57.51 38.0341 57.3599 38.3459C57.2099 38.6573 56.9317 38.8883 56.5986 38.9776C56.1787 39.0929 55.7139 39.2175 55.3025 39.3278L55.1307 39.3739C54.5853 39.5199 54.0143 39.263 53.758 38.7587C53.1614 37.5781 51.8106 35.7124 49.424 36.4169C47.041 37.1214 46.7372 39.3794 46.7701 40.6672C46.7848 41.2173 46.4188 41.7058 45.888 41.8482C45.4048 41.9788 44.8191 42.1365 44.2957 42.276C43.9553 42.3675 43.5929 42.303 43.3037 42.1003C43.0145 41.8972 42.8315 41.5766 42.8022 41.2249C42.6192 38.7569 42.945 33.7604 48.3332 32.3434Z"
                />
                <Path
                    fill="white"
                    fillRule="evenodd"
                    d="M281.593 23.7452V9.30673C281.593 8.82517 281.783 8.36342 282.126 8.02329C282.466 7.68272 282.928 7.49152 283.408 7.49152H287.356C288.361 7.49152 289.171 8.3044 289.171 9.30673V26.1455C290.744 23.9963 293.586 21.9075 297.415 21.8828C306.504 21.8242 308.712 28.9829 309.086 34.3845C309.122 34.7559 309.143 35.141 309.148 35.5406V54.5938C309.148 55.5948 308.337 56.4072 307.337 56.409L303.746 56.416C303.266 56.4169 302.799 56.2262 302.46 55.886C302.261 55.6851 302.112 55.4424 302.023 55.178L301.997 55.1908V55.1C301.953 54.9392 301.927 54.7713 301.927 54.6013V34.5396C301.927 33.0147 301.442 31.3154 300.402 30.0245C298.975 28.3352 296.904 27.714 294.921 28.0387C294.022 28.1894 293.145 28.5357 292.357 29.0437C290.815 30.0579 289.599 32.2283 289.202 34.5396C289.189 34.6929 289.176 34.8484 289.171 35.0053C289.044 38.8075 289.118 49.8513 289.154 54.4488C289.158 54.7537 289.083 55.0515 288.942 55.3159C288.867 55.5595 288.731 55.7842 288.546 55.968C288.25 56.2671 287.841 56.4354 287.418 56.4354L286.271 56.4352L285.342 56.4348H285.339H285.337L285.312 56.4348C284.6 56.4344 283.888 56.4341 283.25 56.4341C282.827 56.4341 282.421 56.2662 282.122 55.9671C281.849 55.6926 281.681 55.3291 281.659 54.9445C281.615 54.7885 281.593 54.6259 281.593 54.4612V23.7452ZM150.408 56.4201C149.757 56.4201 149.23 55.8931 149.225 55.2417L149.222 54.8067V54.8059C149.218 54.1365 149.214 53.3686 149.206 52.6453C146.796 55.1603 143.845 56.4201 140.353 56.4201C136.961 56.4201 134.197 55.4524 132.056 53.5171C129.915 51.5866 128.842 49.1963 128.842 46.346C128.842 42.7484 130.178 39.9892 132.851 38.0682C135.52 36.1425 139.342 35.1796 144.31 35.1796H148.957V32.9665C148.957 31.218 148.468 30.0252 147.491 28.9762C146.514 27.9271 145.029 27.4001 143.031 27.4001C141.306 27.4001 139.893 27.8313 138.791 28.6935C137.939 29.3642 137.412 30.1737 137.22 31.1222C137.115 31.6204 136.674 31.9701 136.171 31.9557C135.217 31.9332 133.715 31.8933 132.384 31.858C131.735 31.8408 131.127 31.8246 130.643 31.8121C130.346 31.8025 130.068 31.6684 129.872 31.4432C129.68 31.2181 129.589 30.9258 129.627 30.6288C129.843 29.2109 130.432 27.86 131.4 26.5906C132.626 24.9762 134.293 23.7163 136.406 22.8014C138.514 21.8864 140.871 21.4266 143.472 21.4266C147.419 21.4266 150.571 22.4181 152.918 24.4061C155.27 26.3893 156.708 29.0959 156.77 32.6935V55.2465C156.77 55.5578 156.645 55.8644 156.42 56.0848C156.2 56.3051 155.898 56.4297 155.582 56.4297C154.169 56.4297 151.821 56.4249 150.408 56.4201ZM141.752 50.0393C143.251 50.0393 144.66 49.6753 145.982 48.9471C147.299 48.219 148.291 47.5388 148.957 46.3172V40.3916H144.875C142.068 40.3916 139.956 40.8802 138.542 41.8574C137.129 42.8347 136.42 43.7784 136.42 45.5652C136.42 47.0214 136.904 47.8837 137.872 48.746C138.839 49.6082 140.133 50.0393 141.752 50.0393ZM269.829 43.073C270.94 43.7777 271.501 44.8085 271.501 46.1605C271.501 47.3878 270.663 49.9 265.307 49.9C260.852 49.9 259.582 47.5412 259.228 45.7146C259.113 45.1249 258.591 44.6934 257.982 44.6934C256.569 44.6934 254.131 44.6934 252.622 44.7078C252.277 44.7078 251.951 44.8564 251.731 45.1201C251.505 45.379 251.415 45.729 251.467 46.0646C252.157 49.3918 255.204 56.3195 265.307 56.3195C277.202 56.3195 278.95 48.7925 278.95 45.7577C278.95 43.9455 278.543 42.4066 277.719 41.1361C276.9 39.8657 275.64 38.7822 273.949 37.876C272.577 37.1433 270.705 36.7491 268.465 36.2778C267.935 36.1663 267.385 36.0505 266.816 35.9248C266.504 35.8563 266.216 35.7935 265.95 35.7354C263.679 35.2396 262.954 35.0814 262.006 34.6351C261.928 34.598 261.848 34.5611 261.767 34.5237C260.741 34.0499 259.568 33.5082 259.568 31.6195C259.568 29.5772 260.952 27.5684 265.609 27.6404C269.34 27.6931 270.658 29.8217 271.022 31.4182C271.161 32.0271 271.702 32.4585 272.33 32.4585H272.348C273.566 32.4585 275.445 32.4585 276.627 32.449C276.909 32.449 277.177 32.3291 277.369 32.1134C277.556 31.9024 277.642 31.6196 277.604 31.3367C277 27.7794 273.671 21.6907 265.211 21.6907C255.774 21.6907 252.593 28.7046 252.593 31.6195C252.593 34.5296 254.686 37.6124 257.838 39.1369C260.995 40.6615 261.81 40.9396 264.387 41.4813L264.775 41.5626C267.145 42.0587 268.774 42.3996 269.829 43.073ZM189.624 38.4223L189.617 38.6865C189.407 45.8098 189.097 56.3602 176.437 56.415C172.503 56.4322 170 54.6033 168.449 52.6868V67.4707C168.449 67.8941 168.282 68.2998 167.982 68.599C167.683 68.8982 167.277 69.0665 166.854 69.0665H162.466C162.043 69.0665 161.638 68.8982 161.338 68.599C161.038 68.2998 160.871 67.8941 160.871 67.4707V22.9097C160.871 22.5995 160.994 22.3021 161.215 22.0832C161.431 21.8638 161.73 21.7404 162.039 21.7404H166.938C167.251 21.7404 167.546 21.8638 167.766 22.0832C167.987 22.3021 168.11 22.5995 168.11 22.9097V25.8586C169.775 23.8904 173.049 21.769 177.477 21.7404C187.465 21.6765 189.884 29.8137 189.624 38.4223ZM182.949 38.4223C182.949 31.2143 181.266 27.6324 176.437 27.6782C171.604 27.724 169.498 31.7875 169.498 35.6167V40.457C169.498 44.1068 170.428 49.9406 176.437 49.9406C182.979 49.9406 182.96 42.5114 182.95 38.8179L182.949 38.6407L182.949 38.4223ZM219.625 50.3773C219.794 50.0671 219.719 49.6848 219.448 49.4627C218.618 48.792 216.885 47.3969 215.992 46.6792C215.852 46.5693 215.678 46.5178 215.5 46.5362C215.323 46.5591 215.164 46.6507 215.056 46.7926C214.222 47.8159 211.917 49.8809 206.861 49.8423C200.133 49.7899 199.271 43.3423 199.204 42.0993C203.082 41.2761 216.595 39.3433 219.489 38.9311C219.807 38.8899 220.049 38.6288 220.072 38.3038C220.215 35.385 220.207 21.8727 207.889 21.8562C194.76 21.8408 192.113 32.2318 192.097 38.5123C192.073 47.7735 196.657 56.3008 206.866 56.3575C215.282 56.404 218.688 51.9386 219.625 50.3773ZM199.641 36.5891C199.641 36.5891 199.09 28.1051 206.692 27.7808C213.8 27.4759 213.716 33.7451 213.675 34.5658C213.676 34.6196 213.632 34.6608 213.584 34.6688C212.395 34.8302 199.641 36.5891 199.641 36.5891ZM249.332 49.4634C249.602 49.6854 249.677 50.0677 249.509 50.3779C248.571 51.9393 245.165 56.4046 236.75 56.3581C226.541 56.3014 221.957 47.7741 221.981 38.513C221.997 32.2324 224.644 21.8414 237.772 21.8568C250.091 21.8733 250.098 35.3856 249.955 38.3044C249.933 38.6295 249.691 38.8905 249.372 38.9317C246.479 39.3439 232.965 41.2767 229.087 42.0999C229.155 43.343 230.017 49.7906 236.744 49.8429C241.801 49.8815 244.105 47.8166 244.939 46.7932C245.048 46.6513 245.206 46.5597 245.384 46.5368C245.562 46.5185 245.736 46.57 245.875 46.6798C246.768 47.3975 248.502 48.7927 249.332 49.4634ZM236.575 27.7814C228.974 28.1057 229.525 36.5897 229.525 36.5897C229.525 36.5897 242.279 34.8309 243.468 34.6694C243.516 34.6614 243.56 34.6202 243.559 34.5664C243.6 33.7457 243.683 27.4766 236.575 27.7814ZM125.945 40.6072C125.778 40.4156 125.533 40.3054 125.279 40.3054H119.957C119.09 40.3054 118.343 40.9186 118.175 41.7713C117.648 44.4395 116.656 46.8011 115.334 47.9844C113.777 49.3736 111.449 50.0729 108.35 50.0729C104.958 50.0729 102.386 48.7844 100.632 46.2024C98.8743 43.6251 97.993 39.8839 97.993 34.9785V30.9546C98.0361 26.1116 98.9797 22.4326 100.819 19.9272C102.659 17.4219 105.294 16.1716 108.723 16.1716C111.674 16.1716 113.916 16.8949 115.444 18.3368C116.79 19.6111 117.686 21.5895 118.132 24.2721C118.247 24.9236 118.812 25.4026 119.473 25.4026C120.934 25.4026 123.416 25.4026 124.91 25.3978C125.231 25.3978 125.533 25.2589 125.744 25.0146C125.955 24.775 126.046 24.4541 125.998 24.138C125.342 19.7739 123.617 16.3489 120.824 13.8627C117.768 11.1417 113.734 9.77647 108.723 9.77647C105.001 9.77647 101.72 10.6627 98.8839 12.4303C96.048 14.198 93.8682 16.7129 92.3497 19.9752C90.8311 23.2422 90.0742 27.0121 90.0742 31.2947V35.539C90.1365 39.7162 90.9175 43.3856 92.4121 46.5473C93.9115 49.7089 96.0335 52.1472 98.788 53.8621C101.547 55.5771 104.733 56.4346 108.35 56.4346C113.528 56.4346 117.677 55.0789 120.796 52.3628C123.699 49.8382 125.543 45.7616 126.156 41.3066C126.19 41.0527 126.113 40.7988 125.945 40.6072Z"
                />
            </G>
        </Svg>
    </View>
)

/**
 * The optional lesson columns in the order they are rendered. Widths are percentages of the row, and
 * the lesson name column takes whatever is left over, so any combination still fills the row and no
 * hand tuned width constants are needed per combination.
 */
const LESSON_COLUMNS = [
    { key: 'status', width: 12, style: styles.colPassed, labelKey: 'status' },
    { key: 'score', width: 14, style: styles.colScore, labelKey: 'score' },
    { key: 'time', width: 16, style: styles.colTime, labelKey: 'time' },
    { key: 'date', width: 20, style: styles.colDate, labelKey: 'date' },
]

export const DEFAULT_PDF_COLUMN_OPTIONS = {
    includeStatus: false,
    includeScore: true,
    includeTime: true,
    includeDate: true,
}

const optionKeyForColumn = {
    status: 'includeStatus',
    score: 'includeScore',
    time: 'includeTime',
    date: 'includeDate',
}

const CourseProgressionPdfDocument = ({
    courseGroupName,
    courseGroupDescription,
    courseGroupProgression,
    studentName,
    generatedDate,
    topics,
    locale = 'en',
    hideScore = false,
    columnOptions,
}) => {
    const t = pdfTranslations[locale] || pdfTranslations.en

    const resolvedOptions = { ...DEFAULT_PDF_COLUMN_OPTIONS, ...(columnOptions || {}) }

    const visibleColumns = LESSON_COLUMNS.filter((column) => {
        // The privacy setting is applied after the picker, so it always wins
        if (column.key === 'score' && hideScore) {
            return false
        }
        return resolvedOptions[optionKeyForColumn[column.key]] === true
    })

    const usedWidth = visibleColumns.reduce((sum, column) => sum + column.width, 0)
    const nameColStyle = { width: `${100 - usedWidth}%` }

    const renderLessonCell = (column, lesson) => {
        if (column.key === 'status') {
            return lesson.hasData ? (
                <Text style={lesson.passed ? styles.passedText : styles.notPassedText}>
                    {lesson.passed ? t.passed : t.notPassed}
                </Text>
            ) : (
                <Text style={[styles.lessonText, { color: Colors.Gray2 }]}>{'—'}</Text>
            )
        }

        if (column.key === 'score') {
            return lesson.hasData ? (
                <Text style={styles.lessonText}>{(lesson.normalizedHighScore * 100).toFixed(0)}%</Text>
            ) : (
                <Text style={[styles.lessonText, { color: Colors.Gray2 }]}>{'—'}</Text>
            )
        }

        if (column.key === 'time') {
            return <Text style={styles.lessonText}>{lesson.timeString || '—'}</Text>
        }

        return <Text style={styles.lessonText}>{lesson.completedDate || '—'}</Text>
    }

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <CapeeshLogoBanner />
                <View style={styles.header}>
                    <Text style={styles.courseTitle}>{courseGroupName}</Text>
                    {courseGroupDescription ? (
                        <Text style={styles.courseDescription}>{courseGroupDescription}</Text>
                    ) : null}
                    <View style={styles.headerMeta}>
                        <Text style={styles.metaText}>{t.student}: {studentName}</Text>
                        <Text style={styles.metaText}>{generatedDate}</Text>
                    </View>
                </View>

                <View style={styles.overallProgressSection}>
                    <Text style={styles.overallProgressLabel}>{t.overallProgression}</Text>
                    <ProgressBar
                        value={courseGroupProgression}
                        height={14}
                        borderRadius={7}
                        fillColor={courseGroupProgression >= 0.99 ? Colors.GreenBright : Colors.BlueBright}
                    />
                    <Text style={styles.overallProgressPercentage}>
                        {Math.round(courseGroupProgression * 100)}%
                    </Text>
                </View>

                {topics.map((topic, topicIdx) => (
                    <View key={topicIdx} style={styles.topicSection} wrap={false}>
                        <View style={styles.topicHeader}>
                            <View style={{ flex: 1 }}>
                                <Text style={styles.topicName}>{topic.topicName}</Text>
                                {topic.topicDescription ? (
                                    <Text style={styles.topicDescription}>{topic.topicDescription}</Text>
                                ) : null}
                                <View style={styles.topicProgressContainer}>
                                    <ProgressBar
                                        value={topic.progression}
                                        width={180}
                                        height={10}
                                        borderRadius={5}
                                        fillColor={topic.progression >= 0.99 ? Colors.GreenBright : Colors.BlueBright}
                                    />
                                    <Text style={styles.topicProgressPercentage}>
                                        {Math.round(topic.progression * 100)}%
                                    </Text>
                                </View>
                            </View>
                        </View>

                        {topic.lessons.length > 0 ? (
                            <View style={styles.lessonTable}>
                                <View style={styles.lessonTableHeader}>
                                    <Text style={[styles.lessonTableHeaderText, nameColStyle]}>{t.lesson}</Text>
                                    {visibleColumns.map((column) => (
                                        <Text
                                            key={column.key}
                                            style={[styles.lessonTableHeaderText, column.style]}>
                                            {t[column.labelKey]}
                                        </Text>
                                    ))}
                                </View>
                                {topic.lessons.map((lesson, lessonIdx) => (
                                    <View
                                        key={lessonIdx}
                                        style={[
                                            styles.lessonRow,
                                            lessonIdx % 2 === 0 ? styles.lessonRowEven : styles.lessonRowOdd,
                                        ]}
                                    >
                                        <View style={[nameColStyle, styles.lessonNameRow]}>
                                            <Text style={styles.lessonText}>{lesson.name}</Text>
                                            {lesson.isExam ? (
                                                <View style={styles.examBadge}>
                                                    <Text style={styles.examBadgeText}>{t.exam}</Text>
                                                </View>
                                            ) : null}
                                        </View>
                                        {visibleColumns.map((column) => (
                                            <View key={column.key} style={column.style}>
                                                {renderLessonCell(column, lesson)}
                                            </View>
                                        ))}
                                    </View>
                                ))}
                            </View>
                        ) : (
                            <Text style={styles.noLessonsText}>{t.noLessonData}</Text>
                        )}
                    </View>
                ))}

                <View style={styles.footer} fixed>
                    <Text style={styles.footerText}>{t.footerTitle}</Text>
                    <Text style={styles.footerText} render={({ pageNumber, totalPages }) =>
                        `${t.page} ${pageNumber} / ${totalPages}`
                    } />
                </View>
            </Page>
        </Document>
    )
}

export default CourseProgressionPdfDocument
